/**
 * VideoStream class manages webcam access and video stream lifecycle
 * Validates Requirements 3.1.1, 4.2.2
 */
export class VideoStream {
  private videoElement: HTMLVideoElement;
  private stream: MediaStream | null = null;

  constructor() {
    this.videoElement = document.createElement('video');
    this.videoElement.setAttribute('playsinline', '');
    this.videoElement.style.display = 'none';
  }

  /**
   * Request camera permissions and initialize webcam video stream
   * @throws Error if camera permission denied or no camera available
   */
  async initialize(): Promise<void> {
    try {
      // Request camera with appropriate constraints
      this.stream = await navigator.mediaDevices.getUserMedia({
        video: {
          width: { ideal: 1280 },
          height: { ideal: 720 },
          facingMode: 'user'
        },
        audio: false
      });

      this.videoElement.srcObject = this.stream;
      
      // Wait for video to be ready
      await new Promise<void>((resolve, reject) => {
        this.videoElement.onloadedmetadata = () => {
          this.videoElement.play()
            .then(() => resolve())
            .catch(reject);
        };
        this.videoElement.onerror = () => reject(new Error('Failed to load video'));
      });
    } catch (error) {
      if (error instanceof Error) {
        if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
          throw new Error('Camera permission denied. Please allow camera access to use this application.');
        } else if (error.name === 'NotFoundError' || error.name === 'DevicesNotFoundError') {
          throw new Error('No camera found. Please connect a camera to use this application.');
        } else if (error.name === 'NotReadableError' || error.name === 'TrackStartError') {
          throw new Error('Camera is already in use by another application. Please close other applications using the camera.');
        }
      }
      throw new Error(`Failed to access camera: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Get the video element for MediaPipe processing
   */
  getVideoElement(): HTMLVideoElement {
    return this.videoElement;
  }

  /**
   * Check if video stream is active
   */
  isActive(): boolean {
    return this.stream !== null && this.stream.active;
  }

  /**
   * Stop video stream and cleanup resources
   */
  cleanup(): void {
    if (this.stream) {
      this.stream.getTracks().forEach(track => track.stop());
      this.stream = null;
    }
    
    if (this.videoElement.srcObject) {
      this.videoElement.srcObject = null;
    }
    
    this.videoElement.pause();
  }
}
